import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DocumentAuditEvent } from '../accounting-lifecycle.model';
import { NewFinanceNote, NewFinanceNoteAction, NewFinanceNoteDraft, NewFinanceNoteKind, NewFinanceNoteSummary, VendorClaimEvidence, VendorClaimEvidenceDraft, SupplierCreditClaimReview } from './new-finance-note.model';

export interface NotePostingSettings {
    owningCompanyId?: number;
    receivableAccountId: number | null;
    payableAccountId: number | null;
    outputVatAccountId: number | null;
    inputVatAccountId: number | null;
    claimSuspenseAccountId: number | null;
}

@Injectable({ providedIn: 'root' })
export class NewFinanceNoteService {
    private readonly base = `${environment.restApiUrl}accounts-endpoint`;
    constructor(private readonly http: HttpClient) {}

    getPostingSettings(companyId: number): Observable<NotePostingSettings> {
        return this.http.get<NotePostingSettings>(`${this.base}/note-posting-settings`, { params: { owningCompanyId: companyId } });
    }
    savePostingSettings(companyId: number, settings: NotePostingSettings): Observable<NotePostingSettings> {
        return this.http.put<NotePostingSettings>(`${this.base}/note-posting-settings`, settings, { params: { owningCompanyId: companyId } });
    }

    list(kind: NewFinanceNoteKind, owningCompanyId: number, page = 0, pageSize = 25,
         crmClientId?: number, status?: string, searchText?: string): Observable<NewFinanceNoteSummary[]> {
        let params = new HttpParams().set('owningCompanyId', owningCompanyId).set('page', page).set('pageSize', pageSize);
        if (crmClientId != null) params = params.set('crmClientId', crmClientId);
        if (status?.trim()) params = params.set('status', status.trim());
        if (searchText?.trim()) params = params.set('searchText', searchText.trim());
        return this.http.get<NewFinanceNoteSummary[]>(`${this.base}/${kind}`, { params });
    }
    get(kind: NewFinanceNoteKind, id: number): Observable<NewFinanceNote> {
        return this.http.get<NewFinanceNote>(`${this.base}/${kind}/${id}`);
    }
    create(kind: NewFinanceNoteKind, draft: NewFinanceNoteDraft): Observable<NewFinanceNote> {
        return this.http.post<NewFinanceNote>(`${this.base}/${kind}`, draft);
    }
    update(kind: NewFinanceNoteKind, id: number, draft: NewFinanceNoteDraft): Observable<NewFinanceNote> {
        return this.http.put<NewFinanceNote>(`${this.base}/${kind}/${id}`, draft);
    }
    workflow(kind: NewFinanceNoteKind, id: number, action: NewFinanceNoteAction, reason?: string): Observable<NewFinanceNote> {
        return this.http.patch<NewFinanceNote>(`${this.base}/${kind}/${id}/workflow/${action}`, { reason });
    }
    auditEvents(kind: NewFinanceNoteKind, id: number): Observable<DocumentAuditEvent[]> {
        return this.http.get<DocumentAuditEvent[]>(`${this.base}/${kind}/${id}/audit-events`);
    }
    supplierEvidence(id: number): Observable<VendorClaimEvidence[]> {
        return this.http.get<VendorClaimEvidence[]>(`${this.base}/vendor-debit-notes/${id}/supplier-evidence`);
    }
    addSupplierEvidence(id: number, evidence: VendorClaimEvidenceDraft): Observable<VendorClaimEvidence> {
        return this.http.post<VendorClaimEvidence>(`${this.base}/vendor-debit-notes/${id}/supplier-evidence`, evidence);
    }
    rejectSupplierEvidence(id: number, evidenceId: number, reason: string): Observable<void> {
        return this.http.post<void>(`${this.base}/vendor-debit-notes/${id}/supplier-evidence/${evidenceId}/reject`, { reason });
    }
    verifySupplierEvidence(id: number, evidenceId: number): Observable<void> {
        return this.http.post<void>(`${this.base}/vendor-debit-notes/${id}/supplier-evidence/${evidenceId}/verify`, {});
    }
    reverseSupplierEvidence(id: number, evidenceId: number, reason: string): Observable<void> {
        return this.http.post<void>(`${this.base}/vendor-debit-notes/${id}/supplier-evidence/${evidenceId}/reverse`, { reason });
    }
    creditClaimReviews(id: number): Observable<SupplierCreditClaimReview[]> {
        return this.http.get<SupplierCreditClaimReview[]>(`${this.base}/credit-notes/${id}/claim-reviews`);
    }
    claimCreditReviews(id: number): Observable<SupplierCreditClaimReview[]> {
        return this.http.get<SupplierCreditClaimReview[]>(`${this.base}/vendor-debit-notes/${id}/credit-reviews`);
    }
    confirmCreditClaimUnrelated(creditId: number, claimId: number, reason: string): Observable<void> {
        return this.http.post<void>(`${this.base}/credit-notes/${creditId}/claim-reviews/${claimId}`, { reason });
    }
}
